
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Filter, Search, X } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";

const eventTypeOptions = [
  { value: "all", label: "All Types" },
  { value: "live", label: "Live Events" },
  { value: "self_paced", label: "Self-Paced" }
];

const sortOptions = [
  { value: "date-asc", label: "Date (Earliest First)" },
  { value: "date-desc", label: "Date (Latest First)" },
  { value: "title-asc", label: "Title (A-Z)" },
  { value: "title-desc", label: "Title (Z-A)" },
  { value: "hours-asc", label: "Hours (Low to High)" },
  { value: "hours-desc", label: "Hours (High to Low)" }
];

interface CourseFiltersProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  eventType: string;
  setEventType: (type: string) => void;
  sortOrder: string;
  setSortOrder: (order: string) => void;
  totalResults: number;
}

export const CourseFilters = ({
  searchQuery,
  setSearchQuery,
  eventType,
  setEventType,
  sortOrder,
  setSortOrder,
  totalResults
}: CourseFiltersProps) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  // Track active filters count
  useEffect(() => {
    let count = 0;
    if (searchQuery) count++;
    if (eventType !== "all") count++;
    setActiveFiltersCount(count);
  }, [searchQuery, eventType]);

  return (
    <div className="space-y-4 mb-6">
      {/* Search Bar with Icon */}
      <div className="relative">
        <div className="flex items-center">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary/60" />
            <Input
              placeholder="Search courses by title, description, category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 font-section h-10 bg-white border-accent-light/30 focus-visible:ring-primary/30"
            />
            {searchQuery && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setSearchQuery('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 rounded-full"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          
          {/* Fixed: Wrapped the filter button in a proper Collapsible component */}
          <Collapsible open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                className="ml-2 gap-1.5 h-10 font-section border-accent-light/30"
              >
                <Filter className="h-4 w-4" />
                <span>Filters</span>
                {activeFiltersCount > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center rounded-full text-xs">
                    {activeFiltersCount}
                  </Badge>
                )}
              </Button>
            </CollapsibleTrigger>
            
            {/* CollapsibleContent moved inside the Collapsible component */}
            <CollapsibleContent className="p-3 mt-2 border rounded-md bg-accent-subtle/10 border-accent-light/20 space-y-3 animate-slide-down">
              <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                <label className="text-sm font-medium whitespace-nowrap w-24">Event Type:</label>
                <Select value={eventType} onValueChange={setEventType}>
                  <SelectTrigger className="w-full sm:w-[200px] font-section text-sm h-9">
                    <SelectValue placeholder="Event Type" />
                  </SelectTrigger>
                  <SelectContent className="font-section text-sm">
                    {eventTypeOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                <label className="text-sm font-medium whitespace-nowrap w-24">Sort By:</label>
                <Select value={sortOrder} onValueChange={setSortOrder}>
                  <SelectTrigger className="w-full sm:w-[200px] font-section text-sm h-9">
                    <SelectValue placeholder="Sort By" />
                  </SelectTrigger>
                  <SelectContent className="font-section text-sm">
                    {sortOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex justify-between pt-2">
                <p className="text-xs text-muted-foreground">
                  {searchQuery && <span>Showing results for "<strong>{searchQuery}</strong>"</span>}
                </p>
                
                {(searchQuery || eventType !== "all") && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      setSearchQuery('');
                      setEventType('all');
                    }}
                    className="text-xs h-7 font-section"
                  >
                    Clear all filters
                  </Button>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </div>

      {/* Result count */}
      {totalResults > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground font-section">
            Found <span className="font-medium text-primary">{totalResults}</span> {totalResults === 1 ? 'course' : 'courses'}
          </p>
        </div>
      )}
    </div>
  );
};
